import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ScaleExplorer } from "@/components/mt/ScaleExplorer";
import { ChordTree } from "@/components/mt/ChordTree";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory32() {
  const { lesson, course, accent } = findLesson("music-theory", "32");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT32-Minor-Harmony-Animated.mp4"
        caption="Harmonising C minor — i · ii° · III · iv · v · VI · VII"
        hint="≈ 22 sec · watch first"
      />
      <Chapter number="32" eyebrow="Music Theory · Chapter Thirty-Two" title="How to harmonise the minor scale">
        <p>
          Most theory books harmonise major and stop. They show you the seven diatonic
          chords (I, ii, iii, IV, V, vi, vii°), they hand you the C major triads
          underneath, and they assume you can mirror the same procedure in any other key.
          What they often skip is what happens when the appeal of the song is
          <em> minor</em> — and the minor scale brings not one but several variants:
          <strong> natural</strong>, <strong>harmonic</strong>, <strong>melodic</strong>,
          and the modal cousins like <strong>Dorian</strong>.
        </p>
        <p>
          We'll harmonise three of them in this chapter — natural minor, harmonic minor,
          and Dorian — using the key of <strong>D</strong> throughout, so the comparisons
          are direct. (Melodic minor lives in jazz and gets its own chapter later.)
        </p>
        <MTQuote>
          Harmonic minor is a natural minor with a raised 7th. Dorian is a natural minor
          with a raised 6. So even though Dorian is not called as a minor scale officially
          in books, I like to package it along with all the other minors while learning
          things, especially harmonization.
        </MTQuote>
        <NoteImage
          src="/notes/music-theory/finals/32a-c-harmonic-minor-scale-and-chords.png"
          caption="Jason's harmonisation of C harmonic minor — i (Cm), ii° (Dm7♭5), ♭III+ (E♭aug), iv (Fm), V (G — the major V is the whole point of harmonic minor), ♭VI (A♭), vii° (B°). The raised 7th (B♮) creates a real V7 chord and gives minor keys their tension and resolution."
        />
      </Chapter>

      <Chapter eyebrow="Three flavours of D minor" title="The notes before the chords">
        <Prose>
          <p>
            Before we harmonise anything, we need to know what notes we're working with.
            All three minors share the same first five notes; they diverge at the 6th and
            7th. (The 6th is what separates Dorian from natural minor; the 7th is what
            separates harmonic minor from natural minor.)
          </p>
        </Prose>

        <Steps>
          <Step><strong>D natural minor:</strong> <span className="font-mono text-amber-300">D E F G A B♭ C D</span> — flat 3, flat 6, flat 7. The same notes as F major (its relative major).</Step>
          <Step><strong>D harmonic minor:</strong> <span className="font-mono text-amber-300">D E F G A B♭ C♯ D</span> — natural minor with a <em>raised 7th</em>. That C♯ is what gives the scale its dramatic, classical, James-Bond feel.</Step>
          <Step><strong>D Dorian:</strong> <span className="font-mono text-amber-300">D E F G A B C D</span> — natural minor with a <em>raised 6th</em> (B natural instead of B♭). Same notes as C major. Brave, modal, rocky.</Step>
        </Steps>

        <ScaleExplorer tonic="D" scales={["naturalMinor", "harmonicMinor", "dorian"]} />

        <Prose>
          <p>
            Click any tile to hear the note. Notice how dramatically the scale changes
            character with a single semitone shift on the 6th or 7th degree. That shift
            is what re-shapes the chords built on those notes.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The harmonisation method" title="Skip and play, written in a circle">
        <Prose>
          <p>
            Every diatonic harmonisation in this course follows the same procedure:
            write the scale in a <strong>circle</strong> (so the cyclical relationships
            are visible to your eye), then build a triad on each note by going
            <em> skip-play, skip-play</em>. That gives you a stack of two thirds —
            interlocking — which is the definition of a triad.
          </p>
          <p>
            Naming the triad is the same drill as always. Look at the bottom third — is
            it major or minor? Look at the top third. Is the outer interval a perfect
            5th, a diminished 5th, or an augmented 5th? Three measurements; the chord
            quality drops out.
          </p>
        </Prose>

        <TheoryBox label="Why a circle, not a line">
          A scale written in a row reads like text. A scale written in a circle reads
          like geometry — you see thirds across, fifths across, and the closure of the
          octave back to the tonic at a glance. Writing scales in lines is exactly why
          most students never internalise them. Use circles. Always.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="D natural minor" title="The most common minor harmonisation">
        <Prose>
          <p>
            Building triads through D natural minor:
          </p>
        </Prose>

        <TheoryBox label="The seven chords of D natural minor">
          <div className="space-y-1 font-mono text-[14px] text-amber-200">
            <div><strong>i</strong> = D minor &nbsp;<span className="text-nsm-dim">(D F A)</span></div>
            <div><strong>ii°</strong> = E diminished &nbsp;<span className="text-nsm-dim">(E G B♭)</span></div>
            <div><strong>♭III</strong> = F major &nbsp;<span className="text-nsm-dim">(F A C) — the relative major's I chord</span></div>
            <div><strong>iv</strong> = G minor &nbsp;<span className="text-nsm-dim">(G B♭ D)</span></div>
            <div><strong>v</strong> = A minor &nbsp;<span className="text-nsm-dim">(A C E) — note: minor, not major</span></div>
            <div><strong>♭VI</strong> = B♭ major &nbsp;<span className="text-nsm-dim">(B♭ D F)</span></div>
            <div><strong>♭VII</strong> = C major &nbsp;<span className="text-nsm-dim">(C E G)</span></div>
          </div>
        </TheoryBox>

        <ChordTree tonic="D" mode="minor" />

        <Prose>
          <p>
            Three minor chords (i, iv, v), three major chords (♭III, ♭VI, ♭VII) and one
            diminished (ii°). The big surprise for first-time minor-harmonisers is that
            the <strong>v is minor</strong>. In natural minor, the dominant is a quiet
            minor chord — modal, not functional, and that's why natural-minor songs
            often feel "incomplete" when they cadence back to the tonic. There's no
            tension to release.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="D harmonic minor" title="Raise the 7, change everything">
        <Prose>
          <p>
            The whole point of harmonic minor is to fix that boring v chord. Raise the
            7th from C to C♯, and the chord on A becomes <strong>A major</strong>: A,
            C♯, E. Now there's a real dominant, full of pull toward the tonic D minor.
            But that single change ripples through the rest of the harmonisation in
            unexpected ways:
          </p>
        </Prose>

        <TheoryBox label="The seven chords of D harmonic minor">
          <div className="space-y-1 font-mono text-[14px] text-amber-200">
            <div><strong>i</strong> = D minor</div>
            <div><strong>ii°</strong> = E diminished</div>
            <div><strong>♭III+</strong> = F augmented &nbsp;<span className="text-nsm-dim">(F A C♯) — the C♯ raises the 5th of F to make it augmented</span></div>
            <div><strong>iv</strong> = G minor</div>
            <div><strong>V</strong> = A major &nbsp;<span className="text-nsm-dim">(A C♯ E) — the dominant returns!</span></div>
            <div><strong>♭VI</strong> = B♭ major</div>
            <div><strong>vii°</strong> = C♯ diminished &nbsp;<span className="text-nsm-dim">(C♯ E G) — leading-tone diminished</span></div>
          </div>
        </TheoryBox>

        <Prose>
          <p>
            Two unusual chords appear in this list: the <strong>♭III augmented</strong>
            (F augmented, very rare and very classical) and the <strong>vii°
            diminished</strong> (C♯ diminished, which often gets stretched to a fully
            diminished 7th in cadences). Extend further to seventh chords and harmonic
            minor offers some of the most cinematic colours in all of music:
          </p>
        </Prose>

        <Steps>
          <Step><strong>i (with 7th)</strong> = D minor major 7 — the spy-thriller chord. Note the <em>major</em> 7 over a minor triad.</Step>
          <Step><strong>ii (with 7th)</strong> = E half-diminished (Eø7 — E G B♭ D) — the ii of a minor cadence. Not E°7: that would need a D♭, which isn't in the scale. The <em>fully</em> diminished chord of harmonic minor lives on the vii (C♯ E G B♭).</Step>
          <Step><strong>♭III (with 7th)</strong> = F augmented major 7 — exotic, harmonically dense.</Step>
          <Step><strong>V7♭9</strong> = A7 with a B♭ on top — a dominant chord that practically <em>announces</em> harmonic minor on its own.</Step>
        </Steps>

        <Prose>
          <p>
            Listen to the i → V → i cadence in D harmonic minor — D minor → A major → D
            minor. That C♯ on the way up creates the dramatic "leading tone" that makes
            classical, flamenco and film-score minor-key writing feel so resolved. It's
            the same chord sequence underneath countless James Bond intros and gypsy
            jazz solos.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="D Dorian" title="The brave modal minor">
        <Prose>
          <p>
            Dorian is technically a mode of the major scale (the second mode), but
            functionally it sits in the minor family — its tonic chord is minor. It is
            the natural minor with a <strong>raised 6th</strong>. So in D Dorian we
            replace the B♭ from natural minor with a B natural; the rest is identical.
          </p>
          <p>
            That single change is enough to give Dorian a totally different character.
            The 6th degree is what colours the iv chord, and raising it converts iv from
            minor (G minor) into <strong>major</strong> (G major). The IV chord in a
            minor key is the magic of Dorian.
          </p>
        </Prose>

        <TheoryBox label="The seven chords of D Dorian">
          <div className="space-y-1 font-mono text-[14px] text-amber-200">
            <div><strong>i</strong> = D minor</div>
            <div><strong>ii</strong> = E minor &nbsp;<span className="text-nsm-dim">(was diminished in natural minor; now minor because B is natural)</span></div>
            <div><strong>♭III</strong> = F major</div>
            <div><strong>IV</strong> = G major &nbsp;<span className="text-nsm-dim">(G B D — the signature Dorian chord)</span></div>
            <div><strong>v</strong> = A minor</div>
            <div><strong>vi°</strong> = B diminished &nbsp;<span className="text-nsm-dim">(B D F)</span></div>
            <div><strong>♭VII</strong> = C major</div>
          </div>
        </TheoryBox>

        <Prose>
          <p>
            Songs in Dorian almost always lean on the <strong>i → IV → i</strong>
            movement (D minor → G major → D minor), because that motion is what makes
            Dorian sound like Dorian and not like natural minor. Famous Dorian songs
            include "Eleanor Rigby" by the Beatles, "Scarborough Fair," "So What" by
            Miles Davis, and a great deal of Celtic and rock writing. Anything that
            wants to sound brave, defiant, or against-the-system tends to find its way
            to Dorian.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="A side-by-side" title="The same v · three different colours">
        <TheoryBox label="The 5-chord across all three minors">
          <div className="space-y-1 font-mono text-[14px] text-amber-200">
            <div><strong>Natural minor:</strong> v = A minor &nbsp;<span className="text-nsm-dim">(modal, gentle)</span></div>
            <div><strong>Harmonic minor:</strong> V = A major &nbsp;<span className="text-nsm-dim">(dramatic, resolves strongly)</span></div>
            <div><strong>Dorian:</strong> v = A minor &nbsp;<span className="text-nsm-dim">(modal — but with the IV major instead)</span></div>
          </div>
          <div className="mt-2 text-[12px] text-nsm-dim">
            Same scale degree. Three different harmonic personalities. That's the entire
            game in minor-key writing — picking which scale to draw the chord from at
            each cadential moment.
          </div>
        </TheoryBox>

        <Glossary>
          <Term name="Natural minor" def="The 6th mode of the major scale. Same notes as the relative major, different tonic. v is minor." />
          <Term name="Harmonic minor" def="Natural minor with a raised 7th. Converts v to V, gives a real dominant cadence, and produces the augmented ♭III." />
          <Term name="Dorian" def="The 2nd mode of the major scale. Natural minor with a raised 6th. Famous for the major IV chord under a minor tonic." />
          <Term name="Modal mixture" def="Borrowing chords across these flavours within the same song. Most modern minor-key writing freely mixes natural and harmonic." />
        </Glossary>

        <MTQuote>
          Now that we've written it in a circle, let's build triads. It's very easy to count
          triads when you've written stuff in a circle, isn't it? So D, F, A. What's the
          logic for building triads? You skip, play, skip, play.
        </MTQuote>
        <NoteImage
          src="/notes/music-theory/finals/32b-c-natural-minor-harmonisation.png"
          caption="C natural minor harmonised — Cm, D°, E♭, Fm, Gm, A♭, B♭ (i ii° ♭III iv v ♭VI ♭VII). No raised 7th means no real dominant — the 'v' is minor, not V. This is why pop-minor songs in natural minor have a different mood than classical-minor songs in harmonic minor."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-33"
        label="Chapter 33 · Understanding Numerator & Denominator of Meters"
        why="With harmony catalogued for both major and minor, we step away from pitch and into time. Time signatures look like fractions — but the fraction lies. We'll see what those two numbers actually mean."
      />
    </LessonShell>
  );
}
